import claudeLogo from '../assets/vendors/claude.svg';
import geminiLogo from '../assets/vendors/gemini.png';
import inklingLogo from '../assets/vendors/inkling.png';
import museGlimmerLogo from '../assets/vendors/muse-glimmer.png';
import palmyraLogo from '../assets/vendors/palmyra.png';

/**
 * Which company made the model — distinct from `model_pricing.provider`
 * (the hosting cloud: bedrock/vertex/azure/fireworks/dashscope). glm-5.2's
 * fireworks row and its dashscope row are both made by Z.AI, hosted by two
 * different clouds — this file answers "made by", not "hosted by".
 *
 * Icons are vendored as plain path data here rather than pulled from
 * @lobehub/icons at runtime: that package's `.Avatar` sub-component imports
 * `Center` from `@lobehub/ui`, which drags in all of antd v6 + antd-style
 * even though nothing else in this app uses them — that silently blew the
 * production build's memory limit (OOM during Vite's chunk rendering, ~12k
 * modules, 2026-09-07 develop deploy). The path data below is copied
 * verbatim from the installed @lobehub/icons Mono components so the mark
 * renders pixel-identical to llmr-website's (same reasoning, different
 * trigger — see that repo's src/lib/models/modelVendor.tsx); keep it in
 * sync by hand if a vendor's icon there ever changes.
 *
 * CUSTOM_LOGOS overrides VENDOR_ICON for the same key: Claude and Writer
 * ship as image assets, Google uses the Gemini sparkle mark, and Inkling
 * (Thinking Machines) uses its ink-blot mark.
 *
 * MODEL_ID_LOGOS is checked first for a single model that needs its own
 * mark without changing siblings from the same maker (Muse Glimmer 30B
 * keeps Meta's infinity on black; other meta-llama/* rows stay on the
 * Meta glyph).
 */
type Vendor =
  | 'anthropic'
  | 'writer'
  | 'inkling'
  | 'zai'
  | 'qwen'
  | 'kimi'
  | 'minimax'
  | 'deepseek'
  | 'nvidia'
  | 'mistral'
  | 'google'
  | 'meta'
  | 'cohere'
  | 'openai'
  | 'xai'
  | 'aws';

const CUSTOM_LOGOS: Partial<Record<Vendor, { src: string; bg?: string }>> = {
  anthropic: { src: claudeLogo, bg: '#D97757' },
  writer: { src: palmyraLogo },
  inkling: { src: inklingLogo },
  google: { src: geminiLogo },
};

/** Exact-model overrides — first match wins. Keep needles specific so a
 * sibling (e.g. Muse Spark) does not inherit the wrong mark. */
const MODEL_ID_LOGOS: { match: RegExp; src: string }[] = [
  { match: /muse-glimmer-30b/i, src: museGlimmerLogo },
];

// Path fill is only set on the handful of icons the library renders as its
// full-color "Color" variant inside Avatar (google, kimi, aws) — everywhere
// else the path inherits `currentColor` from the badge's own `color`
// (background/color below), same as @lobehub/icons' Mono+IconAvatar pairing.
type IconPath = { d: string; clipRule?: 'evenodd'; fill?: string };
type IconDef = {
  viewBox: string;
  paths: IconPath[];
  /** Avatar badge background — a solid color or a CSS gradient string,
   * copied from each vendor's own AVATAR_BACKGROUND. */
  background: string;
  /** Avatar badge icon/text color (AVATAR_COLOR) — white for every vendor
   * except xai, whose badge is white-on-black instead. */
  color: string;
};

const VENDOR_ICON: Partial<Record<Vendor, IconDef>> = {
  zai: {
    viewBox: '0 0 24 24',
    background: '#000',
    color: '#fff',
    paths: [{ d: 'M12.105 2L9.927 4.953H.653L2.83 2h9.276zM23.254 19.048L21.078 22h-9.242l2.174-2.952h9.244zM24 2L9.264 22H0L14.736 2H24z' }],
  },
  qwen: {
    viewBox: '0 0 24 24',
    background: '#615ced',
    color: '#fff',
    paths: [{ d: 'M12.604 1.34c.393.69.784 1.382 1.174 2.075a.18.18 0 00.157.091h5.552c.174 0 .322.11.446.327l1.454 2.57c.19.337.24.478.024.837-.26.43-.513.864-.76 1.3l-.367.658c-.106.196-.223.28-.04.512l2.652 4.637c.172.301.111.494-.043.77-.437.785-.882 1.564-1.335 2.34-.159.272-.352.375-.68.37-.777-.016-1.552-.01-2.327.016a.099.099 0 00-.081.05 575.097 575.097 0 01-2.705 4.74c-.169.293-.38.363-.725.364-.997.003-2.002.004-3.017.002a.537.537 0 01-.465-.271l-1.335-2.323a.09.09 0 00-.083-.049H4.982c-.285.03-.553-.001-.805-.092l-1.603-2.77a.543.543 0 01-.002-.54l1.207-2.12a.198.198 0 000-.197 550.951 550.951 0 01-1.875-3.272l-.79-1.395c-.16-.31-.173-.496.095-.965.465-.813.927-1.625 1.387-2.436.132-.234.304-.334.584-.335a338.3 338.3 0 012.589-.001.124.124 0 00.107-.063l2.806-4.895a.488.488 0 01.422-.246c.524-.001 1.053 0 1.583-.006L11.704 1c.341-.003.724.032.9.34zm-3.432.403a.06.06 0 00-.052.03L6.254 6.788a.157.157 0 01-.135.078H3.253c-.056 0-.07.025-.041.074l5.81 10.156c.025.042.013.062-.034.063l-2.795.015a.218.218 0 00-.2.116l-1.32 2.31c-.044.078-.021.118.068.118l5.716.008c.046 0 .08.02.104.061l1.403 2.454c.046.081.092.082.139 0l5.006-8.76.783-1.382a.055.055 0 01.096 0l1.424 2.53a.122.122 0 00.107.062l2.763-.02a.04.04 0 00.035-.02.041.041 0 000-.04l-2.9-5.086a.108.108 0 010-.113l.293-.507 1.12-1.977c.024-.041.012-.062-.035-.062H9.2c-.059 0-.073-.026-.043-.077l1.434-2.505a.107.107 0 000-.114L9.225 1.774a.06.06 0 00-.053-.031zm6.29 8.02c.046 0 .058.02.034.06l-.832 1.465-2.613 4.585a.056.056 0 01-.05.029.058.058 0 01-.05-.029L8.498 9.841c-.02-.034-.01-.052.028-.054l.216-.012 6.722-.012z' }],
  },
  kimi: {
    // Avatar uses the multi-color variant here, not the plain mono glyph —
    // a blue accent chip plus a white wordmark, both on a black badge.
    viewBox: '0 0 24 24',
    background: '#000',
    color: '#fff',
    paths: [
      { d: 'M21.846 0a1.923 1.923 0 110 3.846H20.15a.226.226 0 01-.227-.226V1.923C19.923.861 20.784 0 21.846 0z', fill: '#1783FF' },
      { d: 'M11.065 11.199l7.257-7.2c.137-.136.06-.41-.116-.41H14.3a.164.164 0 00-.117.051l-7.82 7.756c-.122.12-.302.013-.302-.179V3.82c0-.127-.083-.23-.185-.23H3.186c-.103 0-.186.103-.186.23V19.77c0 .128.083.23.186.23h2.69c.103 0 .186-.102.186-.23v-3.25c0-.069.025-.135.069-.178l2.424-2.406a.158.158 0 01.205-.023l6.484 4.772a7.677 7.677 0 003.453 1.283c.108.012.2-.095.2-.23v-3.06c0-.117-.07-.212-.164-.227a5.028 5.028 0 01-2.027-.807l-5.613-4.064c-.117-.078-.132-.279-.028-.381z', fill: '#fff' },
    ],
  },
  minimax: {
    viewBox: '0 0 24 24',
    background: 'linear-gradient(to right, #E2167E, #FE603C)',
    color: '#fff',
    paths: [{ d: 'M16.278 2c1.156 0 2.093.927 2.093 2.07v12.501a.74.74 0 00.744.709.74.74 0 00.743-.709V9.099a2.06 2.06 0 012.071-2.049A2.06 2.06 0 0124 9.1v6.561a.649.649 0 01-.652.645.649.649 0 01-.653-.645V9.1a.762.762 0 00-.766-.758.762.762 0 00-.766.758v7.472a2.037 2.037 0 01-2.048 2.026 2.037 2.037 0 01-2.048-2.026v-12.5a.785.785 0 00-.788-.753.785.785 0 00-.789.752l-.001 15.904A2.037 2.037 0 0113.441 22a2.037 2.037 0 01-2.048-2.026V18.04c0-.356.292-.645.652-.645.36 0 .652.289.652.645v1.934c0 .263.142.506.372.638.23.131.514.131.744 0a.734.734 0 00.372-.638V4.07c0-1.143.937-2.07 2.093-2.07zm-5.674 0c1.156 0 2.093.927 2.093 2.07v11.523a.648.648 0 01-.652.645.648.648 0 01-.652-.645V4.07a.785.785 0 00-.789-.78.785.785 0 00-.789.78v14.013a2.06 2.06 0 01-2.07 2.048 2.06 2.06 0 01-2.071-2.048V9.1a.762.762 0 00-.766-.758.762.762 0 00-.766.758v3.8a2.06 2.06 0 01-2.071 2.049A2.06 2.06 0 010 12.9v-1.378c0-.357.292-.646.652-.646.36 0 .653.29.653.646V12.9c0 .418.343.757.766.757s.766-.339.766-.757V9.099a2.06 2.06 0 012.07-2.048 2.06 2.06 0 012.071 2.048v8.984c0 .419.343.758.767.758.423 0 .766-.339.766-.758V4.07c0-1.143.937-2.07 2.093-2.07z' }],
  },
  deepseek: {
    viewBox: '0 0 24 24',
    background: '#4D6BFE',
    color: '#fff',
    paths: [{ d: 'M23.748 4.482c-.254-.124-.364.113-.512.234-.051.039-.094.09-.137.136-.372.397-.806.657-1.373.626-.829-.046-1.537.214-2.163.848-.133-.782-.575-1.248-1.247-1.548-.352-.156-.708-.311-.955-.65-.172-.241-.219-.51-.305-.774-.055-.16-.11-.323-.293-.35-.2-.031-.278.136-.356.276-.313.572-.434 1.202-.422 1.84.027 1.436.633 2.58 1.838 3.393.137.093.172.187.129.323-.082.28-.18.552-.266.833-.055.179-.137.217-.329.14a5.526 5.526 0 01-1.736-1.18c-.857-.828-1.631-1.742-2.597-2.458a11.365 11.365 0 00-.689-.471c-.985-.957.13-1.743.388-1.836.27-.098.093-.432-.779-.428-.872.004-1.67.295-2.687.684a3.055 3.055 0 01-.465.137 9.597 9.597 0 00-2.883-.102c-1.885.21-3.39 1.102-4.497 2.623C.082 8.606-.231 10.684.152 12.85c.403 2.284 1.569 4.175 3.36 5.653 1.858 1.533 3.997 2.284 6.438 2.14 1.482-.085 3.133-.284 4.994-1.86.47.234.962.327 1.78.397.63.059 1.236-.03 1.705-.128.735-.156.684-.837.419-.961-2.155-1.004-1.682-.595-2.113-.926 1.096-1.296 2.746-2.642 3.392-7.003.05-.347.007-.565 0-.845-.004-.17.035-.237.23-.256a4.173 4.173 0 001.545-.475c1.396-.763 1.96-2.015 2.093-3.517.02-.23-.004-.467-.247-.588zM11.581 18c-2.089-1.642-3.102-2.183-3.52-2.16-.392.024-.321.471-.235.763.09.288.207.486.371.739.114.167.192.416-.113.603-.673.416-1.842-.14-1.897-.167-1.361-.802-2.5-1.86-3.301-3.307-.774-1.393-1.224-2.887-1.298-4.482-.02-.386.093-.522.477-.592a4.696 4.696 0 011.529-.039c2.132.312 3.946 1.265 5.468 2.774.868.86 1.525 1.887 2.202 2.891.72 1.066 1.494 2.082 2.48 2.914.348.292.625.514.891.677-.802.09-2.14.11-3.054-.614zm1-6.44a.306.306 0 01.415-.287.302.302 0 01.2.288.306.306 0 01-.31.307.303.303 0 01-.304-.308zm3.11 1.596c-.2.081-.399.151-.59.16a1.245 1.245 0 01-.798-.254c-.274-.23-.47-.358-.552-.758a1.73 1.73 0 01.016-.588c.07-.327-.008-.537-.239-.727-.187-.156-.426-.199-.688-.199a.559.559 0 01-.254-.078c-.11-.054-.2-.19-.114-.358.028-.054.16-.186.192-.21.356-.202.767-.136 1.146.016.352.144.618.408 1.001.782.391.451.462.576.685.914.176.265.336.537.445.848.067.195-.019.354-.25.452z' }],
  },
  nvidia: {
    viewBox: '0 0 24 24',
    background: '#74B71B',
    color: '#fff',
    paths: [{ d: 'M10.212 8.976V7.62c.127-.01.256-.017.388-.021 3.596-.117 5.957 3.184 5.957 3.184s-2.548 3.647-5.282 3.647a3.227 3.227 0 01-1.063-.175v-4.109c1.4.174 1.681.812 2.523 2.258l1.873-1.627a4.905 4.905 0 00-3.67-1.846 6.594 6.594 0 00-.729.044m0-4.476v2.025c.13-.01.259-.019.388-.024 5.002-.174 8.261 4.226 8.261 4.226s-3.743 4.69-7.643 4.69c-.338 0-.675-.031-1.007-.092v1.25c.278.038.558.057.838.057 3.629 0 6.253-1.91 8.794-4.169.421.347 2.146 1.193 2.501 1.564-2.416 2.083-8.048 3.763-11.24 3.763-.308 0-.603-.02-.894-.048V19.5H24v-15H10.21zm0 9.756v1.068c-3.356-.616-4.287-4.21-4.287-4.21a7.173 7.173 0 014.287-2.138v1.172h-.005a3.182 3.182 0 00-2.502 1.178s.615 2.276 2.507 2.931m-5.961-3.3c1.436-1.935 3.604-3.148 5.961-3.336V6.523C5.81 6.887 2 10.723 2 10.723s2.158 6.427 8.21 7.015v-1.166C5.77 16 4.25 10.958 4.25 10.958h-.002z' }],
  },
  mistral: {
    viewBox: '0 0 24 24',
    background: '#FA520F',
    color: '#fff',
    paths: [{ clipRule: 'evenodd', d: 'M3.428 3.4h3.429v3.428h3.429v3.429h-.002 3.431V6.828h3.427V3.4h3.43v13.714H24v3.429H13.714v-3.428h-3.428v-3.429h-3.43v3.428h3.43v3.429H0v-3.429h3.428V3.4zm10.286 13.715h3.428v-3.429h-3.427v3.429z' }],
  },
  google: {
    // Avatar uses the multi-color variant — the four brand colors, each on
    // its own path — on a white badge (the badge color is irrelevant here).
    viewBox: '0 0 24 24',
    background: '#fff',
    color: '#fff',
    paths: [
      { d: 'M23 12.245c0-.905-.075-1.565-.236-2.25h-10.54v4.083h6.186c-.124 1.014-.797 2.542-2.294 3.569l-.021.136 3.332 2.53.23.022C21.779 18.417 23 15.593 23 12.245z', fill: '#4285F4' },
      { d: 'M12.225 23c3.03 0 5.574-.978 7.433-2.665l-3.542-2.688c-.948.648-2.22 1.1-3.891 1.1a6.745 6.745 0 01-6.386-4.572l-.132.011-3.465 2.628-.045.124C4.043 20.531 7.835 23 12.225 23z', fill: '#34A853' },
      { d: 'M5.84 14.175A6.65 6.65 0 015.463 12c0-.758.138-1.491.361-2.175l-.006-.147-3.508-2.67-.115.054A10.831 10.831 0 001 12c0 1.772.436 3.447 1.197 4.938l3.642-2.763z', fill: '#FBBC05' },
      { d: 'M12.225 5.253c2.108 0 3.529.892 4.34 1.638l3.167-3.031C17.787 2.088 15.255 1 12.225 1 7.834 1 4.043 3.469 2.197 7.062l3.63 2.763a6.77 6.77 0 016.398-4.572z', fill: '#EB4335' },
    ],
  },
  meta: {
    viewBox: '0 0 24 24',
    background: 'linear-gradient(45deg, #007FF8, #0668E1, #007FF8)',
    color: '#fff',
    paths: [{ d: 'M6.897 4c1.915 0 3.516.932 5.43 3.376l.282-.373c.19-.246.383-.484.58-.71l.313-.35C14.588 4.788 15.792 4 17.225 4c1.273 0 2.469.557 3.491 1.516l.218.213c1.73 1.765 2.917 4.71 3.053 8.026l.011.392.002.25c0 1.501-.28 2.759-.818 3.7l-.14.23-.108.153c-.301.42-.664.758-1.086 1.009l-.265.142-.087.04a3.493 3.493 0 01-.302.118 4.117 4.117 0 01-1.33.208c-.524 0-.996-.067-1.438-.215-.614-.204-1.163-.56-1.726-1.116l-.227-.235c-.753-.812-1.534-1.976-2.493-3.586l-1.43-2.41-.544-.895-1.766 3.13-.343.592C7.597 19.156 6.227 20 4.356 20c-1.21 0-2.205-.42-2.936-1.182l-.168-.184c-.484-.573-.837-1.311-1.043-2.189l-.067-.32a8.69 8.69 0 01-.136-1.288L0 14.468c.002-.745.06-1.49.174-2.23l.1-.573c.298-1.53.828-2.958 1.536-4.157l.209-.34c1.177-1.83 2.789-3.053 4.615-3.16L6.897 4zm-.033 2.615l-.201.01c-.83.083-1.606.673-2.252 1.577l-.138.199-.01.018c-.67 1.017-1.185 2.378-1.456 3.845l-.004.022a12.591 12.591 0 00-.207 2.254l.002.188c.004.18.017.36.04.54l.043.291c.092.503.257.908.486 1.208l.117.137c.303.323.698.492 1.17.492 1.1 0 1.796-.676 3.696-3.641l2.175-3.4.454-.701-.139-.198C9.11 7.3 8.084 6.616 6.864 6.616zm10.196-.552l-.176.007c-.635.048-1.223.359-1.82.933l-.196.198c-.439.462-.887 1.064-1.367 1.807l.266.398c.18.274.362.56.55.858l.293.475 1.396 2.335.695 1.114c.583.926 1.03 1.6 1.408 2.082l.213.262c.282.326.529.54.777.673l.102.05c.227.1.457.138.718.138.176.002.35-.023.518-.073.338-.104.61-.32.813-.637l.095-.163.077-.162c.194-.459.29-1.06.29-1.785l-.006-.449c-.08-2.871-.938-5.372-2.2-6.798l-.176-.189c-.67-.683-1.444-1.074-2.27-1.074z' }],
  },
  cohere: {
    viewBox: '0 0 24 24',
    background: '#39594D',
    color: '#fff',
    paths: [
      { clipRule: 'evenodd', d: 'M8.128 14.099c.592 0 1.77-.033 3.398-.703 1.897-.781 5.672-2.2 8.395-3.656 1.905-1.018 2.74-2.366 2.74-4.18A4.56 4.56 0 0018.1 1H7.549A6.55 6.55 0 001 7.55c0 3.617 2.745 6.549 7.128 6.549z' },
      { clipRule: 'evenodd', d: 'M9.912 18.61a4.387 4.387 0 012.705-4.052l3.323-1.38c3.361-1.394 7.06 1.076 7.06 4.715a5.104 5.104 0 01-5.105 5.104l-3.597-.001a4.386 4.386 0 01-4.386-4.387z' },
      { d: 'M4.776 14.962A3.775 3.775 0 001 18.738v.489a3.776 3.776 0 007.551 0v-.49a3.775 3.775 0 00-3.775-3.775z' },
    ],
  },
  openai: {
    viewBox: '0 0 24 24',
    background: '#000',
    color: '#fff',
    paths: [{ d: 'M9.205 8.658v-2.26c0-.19.072-.333.238-.428l4.543-2.616c.619-.357 1.356-.523 2.117-.523 2.854 0 4.662 2.212 4.662 4.566 0 .167 0 .357-.024.547l-4.71-2.759a.797.797 0 00-.856 0l-5.97 3.473zm10.609 8.8V12.06c0-.333-.143-.57-.429-.737l-5.97-3.473 1.95-1.118a.433.433 0 01.476 0l4.543 2.617c1.309.76 2.189 2.378 2.189 3.948 0 1.808-1.07 3.473-2.76 4.163zM7.802 12.703l-1.95-1.142c-.167-.095-.239-.238-.239-.428V5.899c0-2.545 1.95-4.472 4.591-4.472 1 0 1.927.333 2.712.928L8.23 5.067c-.285.166-.428.404-.428.737v6.898zM12 15.128l-2.795-1.57v-3.33L12 8.658l2.795 1.57v3.33L12 15.128zm1.796 7.23c-1 0-1.927-.332-2.712-.927l4.686-2.712c.285-.166.428-.404.428-.737v-6.898l1.974 1.142c.167.095.238.238.238.428v5.233c0 2.545-1.974 4.472-4.614 4.472zm-5.637-5.303l-4.544-2.617c-1.308-.761-2.188-2.378-2.188-3.948A4.482 4.482 0 014.21 6.327v5.423c0 .333.143.571.428.738l5.947 3.449-1.95 1.118a.432.432 0 01-.476 0zm-.262 3.9c-2.688 0-4.662-2.021-4.662-4.519 0-.19.024-.38.047-.57l4.686 2.71c.286.167.571.167.856 0l5.97-3.448v2.26c0 .19-.07.333-.237.428l-4.543 2.616c-.619.357-1.356.523-2.117.523zm5.899 2.83a5.947 5.947 0 005.827-4.756C22.287 18.339 24 15.84 24 13.296c0-1.665-.713-3.282-1.998-4.448.119-.5.19-.999.19-1.498 0-3.401-2.759-5.947-5.946-5.947-.642 0-1.26.095-1.88.31A5.962 5.962 0 0010.205 0a5.947 5.947 0 00-5.827 4.757C1.713 5.447 0 7.945 0 10.49c0 1.666.713 3.283 1.998 4.448-.119.5-.19 1-.19 1.499 0 3.401 2.759 5.946 5.946 5.946.642 0 1.26-.095 1.88-.309a5.96 5.96 0 004.162 1.713z' }],
  },
  xai: {
    // The one vendor whose badge is dark-on-light instead of white-on-color.
    viewBox: '0 0 24 24',
    background: '#fff',
    color: '#000',
    paths: [{ d: 'M6.469 8.776L16.512 23h-4.464L2.005 8.776H6.47zm-.004 7.9l2.233 3.164L6.467 23H2l4.465-6.324zM22 2.582V23h-3.659V7.764L22 2.582zM22 1l-9.952 14.095-2.233-3.163L17.533 1H22z' }],
  },
  aws: {
    // Avatar uses the multi-color variant — the wordmark inherits the badge
    // color, the swoosh below it is always orange regardless of theme.
    viewBox: '0 0 24 24',
    background: '#222F3E',
    color: '#fff',
    paths: [
      { d: 'M6.763 11.212c0 .296.032.535.088.71.064.176.144.368.256.576.04.063.056.127.056.183 0 .08-.048.16-.152.24l-.503.335a.383.383 0 01-.208.072c-.08 0-.16-.04-.239-.112a2.47 2.47 0 01-.287-.375 6.18 6.18 0 01-.248-.471c-.622.734-1.405 1.101-2.347 1.101-.67 0-1.205-.191-1.596-.574-.39-.384-.59-.894-.59-1.533 0-.678.24-1.23.726-1.644.487-.415 1.133-.623 1.955-.623.272 0 .551.024.846.064.296.04.6.104.918.176v-.583c0-.607-.127-1.03-.375-1.277-.255-.248-.686-.367-1.3-.367-.28 0-.568.031-.863.103-.295.072-.583.16-.862.272-.09.04-.184.075-.28.104a.488.488 0 01-.127.023c-.112 0-.168-.08-.168-.247v-.391c0-.128.016-.224.056-.28a.597.597 0 01.224-.167 4.577 4.577 0 011.005-.36 4.84 4.84 0 011.246-.151c.95 0 1.644.216 2.091.647.44.43.662 1.085.662 1.963v2.586h.016zm-3.24 1.214c.263 0 .534-.048.822-.144a1.78 1.78 0 00.758-.51 1.27 1.27 0 00.272-.512c.047-.191.08-.423.08-.694v-.335a6.66 6.66 0 00-.735-.136 6.02 6.02 0 00-.75-.048c-.535 0-.926.104-1.19.32-.263.215-.39.518-.39.917 0 .375.095.655.295.846.191.2.47.296.838.296zm6.41.862c-.144 0-.24-.024-.304-.08-.064-.048-.12-.16-.168-.311L7.586 6.726a1.398 1.398 0 01-.072-.32c0-.128.064-.2.191-.2h.783c.151 0 .255.025.31.08.065.048.113.16.16.312l1.342 5.284 1.245-5.284c.04-.16.088-.264.151-.312a.549.549 0 01.32-.08h.638c.152 0 .256.025.32.08.063.048.12.16.151.312l1.261 5.348 1.381-5.348c.048-.16.104-.264.16-.312a.52.52 0 01.311-.08h.743c.127 0 .2.065.2.2 0 .04-.009.08-.017.128a1.137 1.137 0 01-.056.2l-1.923 6.17c-.048.16-.104.263-.168.311a.51.51 0 01-.303.08h-.687c-.15 0-.255-.024-.32-.08-.063-.056-.119-.16-.15-.32L12.32 7.747l-1.23 5.14c-.04.16-.087.264-.15.32-.065.056-.177.08-.32.08l-.686.001zm10.256.215c-.415 0-.83-.048-1.229-.143-.399-.096-.71-.2-.918-.32-.128-.071-.215-.151-.247-.223a.563.563 0 01-.048-.224v-.407c0-.167.064-.247.183-.247.048 0 .096.008.144.024.048.016.12.048.2.08.271.12.566.215.878.279.32.064.63.096.95.096.502 0 .894-.088 1.165-.264a.86.86 0 00.415-.758.777.777 0 00-.215-.559c-.144-.151-.416-.287-.807-.415l-1.157-.36c-.583-.183-1.014-.454-1.277-.813a1.902 1.902 0 01-.4-1.158c0-.335.073-.63.216-.886.144-.255.335-.479.575-.654.24-.184.51-.32.83-.415.32-.096.655-.136 1.006-.136.175 0 .36.008.535.032.183.024.35.056.518.088.16.04.312.08.455.127.144.048.256.096.336.144a.69.69 0 01.24.2.43.43 0 01.071.263v.375c0 .168-.064.256-.184.256a.83.83 0 01-.303-.096 3.652 3.652 0 00-1.532-.311c-.455 0-.815.071-1.062.223-.248.152-.375.383-.375.71 0 .224.08.416.24.567.16.152.454.304.877.44l1.134.358c.574.184.99.44 1.237.767.247.327.367.702.367 1.117 0 .343-.072.655-.207.926a2.157 2.157 0 01-.583.703c-.248.2-.543.343-.886.447-.36.111-.734.167-1.142.167z' },
      { d: 'M.378 15.475c3.384 1.963 7.56 3.153 11.877 3.153 2.914 0 6.114-.607 9.06-1.852.44-.2.814.287.383.607-2.626 1.94-6.442 2.969-9.722 2.969-4.598 0-8.74-1.7-11.87-4.526-.247-.223-.024-.527.272-.351zm23.531-.2c.287.36-.08 2.826-1.485 4.007-.215.184-.423.088-.327-.151l.175-.439c.343-.88.802-2.198.52-2.555-.336-.43-2.22-.207-3.074-.103-.255.032-.295-.192-.063-.36 1.5-1.053 3.967-.75 4.254-.399z', fill: '#F90' },
    ],
  },
};

// model_id's own vendor/ prefix, where it has one — covers the large
// majority of the catalog (anthropic/claude-*, openai/gpt-*, google/gemini-*,
// meta-llama/llama-*, mistralai/mistral-*, qwen/qwen*, zai/glm-*,
// deepseek/deepseek-*, moonshot/kimi-*, cohere/embed-*, xai/grok-*,
// amazon/nova-*).
const PREFIX_VENDOR: Record<string, Vendor> = {
  anthropic: 'anthropic',
  openai: 'openai',
  google: 'google',
  'meta-llama': 'meta',
  mistralai: 'mistral',
  qwen: 'qwen',
  zai: 'zai',
  deepseek: 'deepseek',
  moonshot: 'kimi',
  cohere: 'cohere',
  xai: 'xai',
  amazon: 'aws',
  thinkingmachines: 'inkling',
};

// Fallback for the model_ids with no vendor/ prefix at all — mostly the
// fw- prefixed Fireworks entries from before they were unified with a
// second provider, plus the already-unified bare ones (glm-5.2,
// kimi-k2.7-code, deepseek-v4-pro). Order matters: checked top to bottom,
// first substring match wins.
const KEYWORD_VENDOR: [string, Vendor][] = [
  ['inkling', 'inkling'],
  ['glm', 'zai'],
  ['kimi', 'kimi'],
  ['deepseek', 'deepseek'],
  ['minimax', 'minimax'],
  ['nemotron', 'nvidia'],
  ['qwen', 'qwen'],
  ['gpt-oss', 'openai'],
  ['claude', 'anthropic'],
  ['llama', 'meta'],
  ['mistral', 'mistral'],
  ['gemini', 'google'],
];

/** Infers which company made a model from its model_id. Returns null rather
 * than guess when nothing matches (e.g. mock-a, qa-e2e/* test fixtures) —
 * showing no badge is always safer than showing a wrong one. */
export function inferVendor(modelId: string): Vendor | null {
  const slash = modelId.indexOf('/');
  if (slash >= 0) {
    const prefix = modelId.slice(0, slash);
    if (PREFIX_VENDOR[prefix]) return PREFIX_VENDOR[prefix];
  }
  const lower = modelId.toLowerCase();
  for (const [needle, vendor] of KEYWORD_VENDOR) {
    if (lower.includes(needle)) return vendor;
  }
  return null;
}

/** Small inline vendor mark for a model_id — renders nothing when the
 * vendor can't be inferred, rather than a placeholder every row doesn't need. */
export function ModelVendorMark({ modelId, size = 18 }: { modelId: string; size?: number }) {
  const modelLogo = MODEL_ID_LOGOS.find((entry) => entry.match.test(modelId));
  if (modelLogo) {
    return (
      <img
        className="model-vendor-mark model-vendor-mark--full"
        style={{ width: size, height: size }}
        src={modelLogo.src}
        alt=""
      />
    );
  }

  const vendor = inferVendor(modelId);
  if (!vendor) return null;

  const custom = CUSTOM_LOGOS[vendor];
  if (custom) {
    const inner = Math.round(size * 0.6);
    if (!custom.bg) {
      return (
        <img
          className="model-vendor-mark model-vendor-mark--full"
          style={{ width: size, height: size }}
          src={custom.src}
          alt=""
        />
      );
    }
    return (
      <span className="model-vendor-mark" style={{ width: size, height: size, background: custom.bg }}>
        <img style={{ width: inner, height: inner }} src={custom.src} alt="" />
      </span>
    );
  }

  const icon = VENDOR_ICON[vendor];
  if (!icon) return null;
  // Mirrors @lobehub/icons' IconAvatar: a circular badge in the vendor's
  // brand color, containing the glyph scaled down and inheriting the
  // badge's own color via currentColor (paths that need their own color,
  // e.g. google's four brand colors, set `fill` directly instead).
  const inner = Math.round(size * 0.75);
  return (
    <span
      className="model-vendor-mark model-vendor-mark--full"
      style={{ width: size, height: size, background: icon.background, color: icon.color }}
    >
      <svg
        width={inner}
        height={inner}
        viewBox={icon.viewBox}
        fill="currentColor"
        fillRule="evenodd"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        {icon.paths.map((p, i) => (
          <path key={i} d={p.d} clipRule={p.clipRule} fill={p.fill} />
        ))}
      </svg>
    </span>
  );
}
